import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../../shared/services/api.service';
import { AppService } from '../../../shared/services/app.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  CreateModelInput,
  CreateModelMutation,
  CreateModelMutationVariables,
  Engagements,
  ExecutionStatus,
  GetQueryRecordsQuery,
  GetQueryRecordsQueryVariables,
  ModelStatus,
  ModelType,
  QueryRecord,
  QueryType,
} from '../../../../API';
import { GetQueryRecords } from '../../../../graphql/queries';
import { CreateModel } from '../../../../graphql/mutations';
import { v4 as uuidv4 } from 'uuid';
import { AT_LEAST_ONE_ALPHABET, NOT_ALLOWED_SPECIAL_CHARS } from '../../../shared/utils/constants';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-build-model',
  templateUrl: './build-model.component.html',
  styleUrl: './build-model.component.scss',
})
export class BuildModelComponent implements OnInit, OnDestroy {
  modelForm: FormGroup;
  isLoading = false;
  engagement!: string;
  availableQueries?: QueryRecord[];
  loadingQueries: boolean = true;
  seedAudienceCount: number | null = null;
  showCustomTemplate: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private appService: AppService,
    private userManagementService: UserManagementService,
    private nzMessageService: NzMessageService,
    private router: Router,
  ) {
    this.modelForm = this.fb.group({
      modelName: this.fb.control('', [
        Validators.required,
        Validators.maxLength(30),
        Validators.pattern(AT_LEAST_ONE_ALPHABET),
        Validators.pattern(NOT_ALLOWED_SPECIAL_CHARS),
      ]),
      seedAudience: this.fb.control('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.appService.activeEngagement$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => prev.engagement !== curr.engagement),
      )
      .subscribe((engagement: Engagements) => {
        this.engagement = engagement.engagement;
        this.getQueryResults();
      });

    this.modelForm
      .get('seedAudience')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((queryId: string | null) => {
        this.getSeedAudienceSize(queryId);
      });
  }

  getQueryResults(): void {
    this.apiService
      .query<GetQueryRecordsQuery, GetQueryRecordsQueryVariables>(GetQueryRecords, {
        engagementId: this.engagement,
      })
      .then((response) => {
        const result = response.data?.GetQueryRecords;
        if (result) {
          this.availableQueries = result?.filter((row) => this.filterLookalikeQueries(row)) as QueryRecord[];
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`${error} Failed to get query results`);
      })
      .finally(() => {
        this.loadingQueries = false;
      });
  }

  filterLookalikeQueries(query: QueryRecord | null): boolean {
    if (query?.type === QueryType.LIST && query.executionStatus === ExecutionStatus.SUCCESS && query.metadata) {
      try {
        return JSON.parse(query.metadata)?.seedAudienceSize;
      } catch (error) {
        console.warn('Invalid metadata JSON:', error);
      }
    }
    return false;
  }

  getDisplayName(option: QueryRecord): string {
    return `${option.name} ( ${option.queryId} )`;
  }

  getSeedAudienceCount(query: QueryRecord): number {
    if (query.metadata) {
      try {
        const metadata = JSON.parse(query.metadata);
        return metadata.seedAudienceSize ? metadata.seedAudienceSize : 0;
      } catch {
        return 0;
      }
    }
    return 0;
  }

  getSeedAudienceSize(queryId: string | null): void {
    this.showCustomTemplate = false;
    this.seedAudienceCount = null;
    if (queryId && Array.isArray(this.availableQueries)) {
      const selectedQuery = this.availableQueries?.find((q) => q.queryId === queryId);
      if (selectedQuery) {
        this.seedAudienceCount = this.getSeedAudienceCount(selectedQuery);
        this.showCustomTemplate = true;
      } else {
        this.seedAudienceCount = null;
        this.showCustomTemplate = true;
      }
    } else {
      this.seedAudienceCount = null;
      this.showCustomTemplate = true;
    }
  }

  getDisabledTooltipMessage(query: any): string {
    const count = this.getSeedAudienceCount(query);
    return `This audience has only ${count} records. Minimum 1000 required.`;
  }

  createModel(): void {
    this.isLoading = true;
    const selectedQuery = this.availableQueries?.find((q) => q.queryId === this.seedAudienceControl.value);

    if (!selectedQuery) {
      this.nzMessageService.error('Selected query not found');
      this.isLoading = false;
      return;
    }

    const createModelInput: CreateModelInput = {
      modelId: uuidv4(),
      name: this.modelNameControl.value,
      engagement: this.engagement,
      queryId: selectedQuery.queryId,
      queryName: selectedQuery.name,
      athenaExecutionId: selectedQuery.athenaExecutionId || '',
      seedAudienceSize: this.getSeedAudienceCount(selectedQuery),
      createdBy: this.userManagementService.getEmail(),
      orgType: this.userManagementService.getOrgType(),
      status: ModelStatus.IN_PROGRESS,
      modelType: ModelType.BASE,
    };

    this.apiService
      .query<CreateModelMutation, CreateModelMutationVariables>(CreateModel, {
        input: createModelInput,
      })
      .then((response) => {
        if (response.data?.CreateModel?.success) {
          const modelName = this.modelNameControl.value;
          this.nzMessageService.success(
            `Model training started successfully. Your lookalike model "${modelName}" has been queued for training. This process may take up to 2 hours.`,
          );
          this.router.navigate(['/ml/history']);
        } else {
          this.nzMessageService.error(response.data?.CreateModel?.statusMessage || 'Failed to create model');
        }
      })
      .catch((error) => {
        this.nzMessageService.error(error?.message ?? 'Failed to create model');
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  get modelNameControl(): UntypedFormControl {
    return this.modelForm.get('modelName') as UntypedFormControl;
  }

  get seedAudienceControl(): UntypedFormControl {
    return this.modelForm.get('seedAudience') as UntypedFormControl;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
